<template>
  <div class="agent-container">
    <!-- 搜索表单 -->
    <el-card class="filter-card" shadow="never">
      <el-form :inline="true" :model="listQuery" class="filter-form">
        <el-form-item label="店铺名称">
          <el-input
            v-model="listQuery.agent_name"
            placeholder="请输入店铺名称"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleFilter"
          />
        </el-form-item>

        <el-form-item label="状态">
          <el-select
            v-model="listQuery.status"
            placeholder="请选择状态"
            clearable
            style="width: 120px"
          >
            <el-option label="全部" :value="undefined" />
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleFilter">
            查询
          </el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="listLoading"
        :data="agentList"
        border
        fit
        highlight-current-row
        style="width: 100%"
      >
        <!-- ID -->
        <el-table-column
          prop="id"
          label="ID"
          min-width="100"
          align="center"
        />

        <!-- 店铺编码 -->
        <el-table-column
          prop="agent_code"
          label="店铺编码"
          min-width="180"
          align="center"
        />

        <!-- 店铺名称 -->
        <el-table-column
          prop="agent_name"
          label="店铺名称"
          min-width="220"
          align="center"
          show-overflow-tooltip
        />

        <!-- 状态 -->
        <el-table-column
          prop="status"
          label="状态"
          min-width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-tag :type="getAgentStatusTagType(row.status)" size="small">
              {{ getAgentStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- 创建时间 -->
        <el-table-column
          prop="created_at"
          label="创建时间"
          min-width="200"
          align="center"
        >
          <template slot-scope="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>

        <!-- 操作列 -->
        <el-table-column
          label="操作"
          min-width="150"
          align="center"
          fixed="right"
        >
          <template slot-scope="{ row }">
            <el-button
              type="text"
              size="small"
              @click="handleToggleStatus(row)"
            >
              {{ row.status === 1 ? '禁用' : '启用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="listQuery.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="listQuery.page_size"
          :total="total"
          background
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 新增/编辑店铺弹窗 -->
    <agent-dialog
      ref="agentDialog"
      @success="fetchAgentList"
    />

    <!-- 分佣配置弹窗 -->
    <commission-dialog
      ref="commissionDialog"
    />
  </div>
</template>

<script>
import { getAgentList, updateAgentStatus } from '@/api/iccid-agent'
import AgentDialog from './components/AgentDialog'
import CommissionDialog from './components/CommissionDialog'
import { formatDate } from '@/utils/iccid-formatter'

// 店铺状态常量
const AGENT_STATUS_MAP = {
  0: '禁用',
  1: '启用'
}

const AGENT_STATUS_TAG_TYPE = {
  0: 'danger',
  1: 'success'
}

export default {
  name: 'IccidAgentList',
  components: {
    AgentDialog,
    CommissionDialog
  },
  data() {
    return {
      agentList: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        page_size: 20,
        agent_name: '',
        status: undefined
      }
    }
  },
  created() {
    this.fetchAgentList()
  },
  methods: {
    // 格式化函数
    formatDate,
    getAgentStatusText(status) {
      return AGENT_STATUS_MAP[status] || '未知'
    },
    getAgentStatusTagType(status) {
      return AGENT_STATUS_TAG_TYPE[status] || 'info'
    },

    // 获取店铺列表
    async fetchAgentList() {
      this.listLoading = true
      try {
        const { data } = await getAgentList(this.listQuery)
        this.agentList = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.$message.error('获取店铺列表失败')
      } finally {
        this.listLoading = false
      }
    },

    // 搜索
    handleFilter() {
      this.listQuery.page = 1
      this.fetchAgentList()
    },

    // 重置
    handleReset() {
      this.listQuery = {
        page: 1,
        page_size: 20,
        agent_name: '',
        status: undefined
      }
      this.fetchAgentList()
    },

    // 分页
    handleSizeChange(val) {
      this.listQuery.page_size = val
      this.fetchAgentList()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchAgentList()
    },

    // 切换状态
    handleToggleStatus(row) {
      const status = row.status === 1 ? 0 : 1
      const statusText = status === 1 ? '启用' : '禁用'

      this.$confirm(`确认${statusText}该店铺吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return updateAgentStatus({
          agent_id: row.id,
          status: status
        })
      }).then(() => {
        this.$message.success(`${statusText}成功`)
        this.fetchAgentList()
      }).catch(() => {})
    },

    // 分佣配置
    handleCommissions(row) {
      this.$refs.commissionDialog.open(row)
    }
  }
}
</script>

<style scoped>
.agent-container {
  padding: 20px;
}

.filter-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
